import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {catchError, forkJoin, map, Observable, of} from "rxjs";
import {IBookingReference} from "../../../_contracts/Booking/BookingReferences/booking-reference";
import {BookingReferenceType} from "../../../_models/_booking/_enums/booking-reference-type";
import {ServerEndpoints} from "../../../server.endpoints";
import {DtoBaseHttpService} from "../../../_services/dto-services/dto-base-http.service";

@Injectable({
  providedIn: 'root'
})
export class BookingReferenceHttpService extends DtoBaseHttpService<IBookingReference> {
  UrlPath: string = "/Booking/Reference";

  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
    super(baseUrl, httpClient);
  }

  list(type: BookingReferenceType): Observable<IBookingReference[]> {
    return this.httpClient.post<IBookingReference[]>(
      this.baseUrl + ServerEndpoints.Booking_Reference_List_$type$_v2025_4.dictReplace({type: type as any as string}) + "?active=true",
      undefined);
  }

  // The live SignalR group (DtoServices.BookingReferenceService) is shard-scoped, not type-scoped, so in
  // principle the local Entities array should hold every active reference regardless of type. In practice
  // Band isn't implemented anywhere in the system yet (no backing service — BookingReferenceServiceFactory
  // only ever wired up Table), so fetching it here would just 500 and, via forkJoin's fail-fast semantics,
  // take the Table fetch down with it. Table-only until Band actually exists.
  listAllTypes(): Observable<IBookingReference[]> {
    return this.list(BookingReferenceType.Table);
  }

  get(id: string): Observable<IBookingReference> {
    return this.httpClient.get<IBookingReference>(
      this.baseUrl + ServerEndpoints.Booking_Reference_Get_$id$_v2025_4.dictReplace({id: id}));
  }

  /**
   * Sets (or clears — `null`/`0`) the spending limit of an open booking reference from the web app (#204). The
   * web-management action (`v2025_4.ReferenceController.SetSpendingLimit`, gated to ShardAdmin/CompanyOwner/
   * PlatformOwner) — distinct from the register's own step-up-gated action of the same name. The body is the
   * raw limit value itself (matches `[FromBody] double? limit`), not a wrapping object.
   */
  setSpendingLimit(id: string, limit: number | null): Observable<IBookingReference> {
    return this.httpClient.post<IBookingReference>(
      this.baseUrl + ServerEndpoints.Booking_Reference_SetSpendingLimit_$id$_v2025_4.dictReplace({id: id}), limit);
  }

  // DtoBaseService.ProcessUpdate calls Fetch(ids) for every non-Remove push. A reference id carries no type,
  // so the base class's single "/List?all=" POST doesn't apply here — fetch each by id instead. A 404 (the
  // reference closed between the push firing and this fetch running) drops just that id, not the whole batch.
  override Fetch(ids: string[], all: boolean = false): Observable<IBookingReference[]> {
    if (ids.length === 0) return of([]);
    return forkJoin(ids.map(id => this.get(id).pipe(
      catchError(() => of(undefined))
    ))).pipe(map(results => results.filter((r): r is IBookingReference => r !== undefined)));
  }
}
